import { useState, useEffect } from 'react';
import { adminApi } from '../../utils/api';
import {
    Button, Badge, Card, Modal, Alert, SearchBar, Spinner, InfoRow, Input, Select, Textarea,
} from './shared';

const MODALITY_OPTIONS = [
    { value: 'default', label: 'Default' },
    { value: 'text', label: 'Text' },
    { value: 'audio', label: 'Audio' },
    { value: 'image', label: 'Image' },
    { value: 'video', label: 'Video' },
];

const INITIAL_FORM = {
    model_name: '', provider: '', modality: 'default', tier_name: '', notes: '',
    visible_to_user: true, display_name: '', display_order: 0,
    pricing_info: {
        unit: 'tokens', base_unit_size: 1000000,
        input_cost_per_unit: '', output_cost_per_unit: '',
        cache_cost_per_unit: '', up_to_tokens: '',
    },
};

export default function AdminModelCosts() {
    const [costs, setCosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [selectedCost, setSelectedCost] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [isCreateMode, setIsCreateMode] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [providerFilter, setProviderFilter] = useState('');

    useEffect(() => { loadCosts(); }, []);

    const loadCosts = async () => {
        try {
            setLoading(true);
            const res = await adminApi.listModelCosts({ provider: providerFilter || undefined });
            setCosts(res.costs || []);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to load model costs');
        } finally {
            setLoading(false);
        }
    };

    const handleView = (cost) => {
        setSelectedCost(cost);
        setIsViewMode(true);
        setIsCreateMode(false);
        setIsEditMode(false);
        setIsModalOpen(true);
    };

    const handleEdit = (cost) => {
        setSelectedCost({
            ...cost,
            pricing_info: { ...INITIAL_FORM.pricing_info, ...cost.pricing_info },
        });
        setIsEditMode(true);
        setIsViewMode(false);
        setIsCreateMode(false);
        setIsModalOpen(true);
    };

    const handleCreate = () => {
        setSelectedCost(INITIAL_FORM);
        setIsCreateMode(true);
        setIsViewMode(false);
        setIsEditMode(false);
        setIsModalOpen(true);
    };

    const handleSave = async (data) => {
        try {
            const pricing = { ...data.pricing_info };
            pricing.input_cost_per_unit = parseFloat(pricing.input_cost_per_unit) || 0;
            pricing.output_cost_per_unit = parseFloat(pricing.output_cost_per_unit) || 0;
            if (pricing.cache_cost_per_unit) pricing.cache_cost_per_unit = parseFloat(pricing.cache_cost_per_unit);
            else delete pricing.cache_cost_per_unit;
            if (pricing.up_to_tokens) pricing.up_to_tokens = parseInt(pricing.up_to_tokens);
            else delete pricing.up_to_tokens;
            pricing.base_unit_size = parseInt(pricing.base_unit_size) || 1000000;

            const payload = {
                model_name: data.model_name,
                provider: data.provider,
                modality: data.modality,
                tier_name: data.tier_name || null,
                pricing_info: pricing,
                notes: data.notes || null,
                visible_to_user: data.visible_to_user,
                display_name: data.display_name || null,
                display_order: parseInt(data.display_order) || 0,
            };

            if (isEditMode) {
                await adminApi.updateModelCost(data.id, payload);
                setSuccessMessage('Model cost updated');
            } else {
                await adminApi.createModelCost(payload);
                setSuccessMessage('Model cost created');
            }
            setIsModalOpen(false);
            loadCosts();
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleToggleVisibility = async (cost) => {
        const newValue = !cost.visible_to_user;
        setCosts(prev => prev.map(c => c.id === cost.id ? { ...c, visible_to_user: newValue } : c));
        try {
            await adminApi.updateModelCost(cost.id, { visible_to_user: newValue });
        } catch (err) {
            setCosts(prev => prev.map(c => c.id === cost.id ? { ...c, visible_to_user: !newValue } : c));
            setError(err.message);
        }
    };

    const handleDelete = async (costId) => {
        if (!confirm('Delete this pricing rule? This cannot be undone.')) return;
        try {
            await adminApi.deleteModelCost(costId);
            setSuccessMessage('Model cost deleted');
            loadCosts();
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const providers = [...new Set(costs.map(c => c.provider))].sort();

    const filteredCosts = costs.filter(c => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return c.model_name.toLowerCase().includes(q) || c.provider.toLowerCase().includes(q);
    });

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Model Costs</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {costs.length} pricing rule{costs.length !== 1 ? 's' : ''} — LLM pricing with multi-modality and tiered support
                    </p>
                </div>
                <Button size="sm" onClick={handleCreate}>Add Pricing Rule</Button>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">{successMessage}</Alert>}

            <div className="flex flex-wrap items-center gap-2 mb-4">
                <SearchBar
                    value={searchQuery}
                    onChange={setSearchQuery}
                    onRefresh={loadCosts}
                    placeholder="Search models..."
                    className="flex-1"
                />
                {providers.length > 1 && (
                    <select
                        value={providerFilter}
                        onChange={(e) => { setProviderFilter(e.target.value); }}
                        className="h-8 px-3 text-xs border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-gray-400"
                    >
                        <option value="">All Providers</option>
                        {providers.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                )}
            </div>

            {loading ? (
                <Spinner size="lg" text="Loading model costs..." center />
            ) : filteredCosts.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">No pricing rules found</div>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white divide-y divide-gray-100">
                    {filteredCosts.map((cost) => (
                        <div key={cost.id} className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-900 font-mono">{cost.model_name}</span>
                                    <Badge variant="outline">{cost.provider}</Badge>
                                    <Badge variant="info">{cost.modality}</Badge>
                                    {cost.tier_name && <Badge variant="purple">{cost.tier_name}</Badge>}
                                    {cost.visible_to_user ? (
                                        <Badge variant="success">Visible</Badge>
                                    ) : (
                                        <Badge variant="outline">Hidden</Badge>
                                    )}
                                </div>
                                <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                                    <span>In: ${cost.pricing_info?.input_cost_per_unit || 0}/M</span>
                                    <span>Out: ${cost.pricing_info?.output_cost_per_unit || 0}/M</span>
                                    {cost.pricing_info?.cache_cost_per_unit && <span>Cache: ${cost.pricing_info.cache_cost_per_unit}/M</span>}
                                    {cost.display_name && <span>Label: {cost.display_name}</span>}
                                </div>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <Button
                                    variant="ghost"
                                    size="xs"
                                    onClick={() => handleToggleVisibility(cost)}
                                    title={cost.visible_to_user ? 'Hide from users' : 'Show to users'}
                                >
                                    {cost.visible_to_user ? 'Hide' : 'Show'}
                                </Button>
                                <Button variant="ghost" size="xs" onClick={() => handleView(cost)}>View</Button>
                                <Button variant="ghost" size="xs" onClick={() => handleEdit(cost)}>Edit</Button>
                                <Button variant="ghost" size="xs" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(cost.id)}>Delete</Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {isModalOpen && isViewMode && selectedCost && (
                <Modal open onClose={() => setIsModalOpen(false)} title={selectedCost.model_name} size="xl">
                    <Modal.Body>
                        <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                            <InfoRow label="ID" value={selectedCost.id} mono />
                            <InfoRow label="Model Name" value={selectedCost.model_name} mono />
                            <InfoRow label="Provider" value={selectedCost.provider} />
                            <InfoRow label="Modality" value={selectedCost.modality} />
                            <InfoRow label="Tier" value={selectedCost.tier_name || 'Flat pricing'} />
                            <InfoRow label="Visible to User" value={selectedCost.visible_to_user ? 'Yes' : 'No'} />
                            <InfoRow label="Display Name" value={selectedCost.display_name || '—'} />
                            <InfoRow label="Display Order" value={selectedCost.display_order} />
                        </div>
                        <Card title="Pricing Info">
                            <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                                <InfoRow label="Unit" value={selectedCost.pricing_info?.unit || 'tokens'} />
                                <InfoRow label="Base Unit Size" value={(selectedCost.pricing_info?.base_unit_size || 1000000).toLocaleString()} />
                                <InfoRow label="Input Cost/Unit" value={`$${selectedCost.pricing_info?.input_cost_per_unit || 0}`} mono />
                                <InfoRow label="Output Cost/Unit" value={`$${selectedCost.pricing_info?.output_cost_per_unit || 0}`} mono />
                                {selectedCost.pricing_info?.cache_cost_per_unit != null && (
                                    <InfoRow label="Cache Cost/Unit" value={`$${selectedCost.pricing_info.cache_cost_per_unit}`} mono />
                                )}
                                {selectedCost.pricing_info?.up_to_tokens != null && (
                                    <InfoRow label="Up To Tokens" value={selectedCost.pricing_info.up_to_tokens.toLocaleString()} />
                                )}
                            </div>
                        </Card>
                        {selectedCost.notes && <InfoRow label="Notes" value={selectedCost.notes} />}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="ghost" onClick={() => setIsModalOpen(false)}>Close</Button>
                        <Button onClick={() => { setIsViewMode(false); setIsEditMode(true); }}>Edit</Button>
                    </Modal.Footer>
                </Modal>
            )}

            {isModalOpen && (isCreateMode || isEditMode) && selectedCost && (
                <CostEditModal
                    cost={selectedCost}
                    isEdit={isEditMode}
                    onSave={handleSave}
                    onClose={() => setIsModalOpen(false)}
                />
            )}
        </div>
    );
}

function CostEditModal({ cost, isEdit, onSave, onClose }) {
    const [formData, setFormData] = useState({
        ...cost,
        tier_name: cost.tier_name || '',
        notes: cost.notes || '',
        display_name: cost.display_name || '',
        display_order: cost.display_order ?? 0,
        visible_to_user: cost.visible_to_user ?? true,
        pricing_info: {
            unit: 'tokens',
            base_unit_size: 1000000,
            input_cost_per_unit: '',
            output_cost_per_unit: '',
            cache_cost_per_unit: '',
            up_to_tokens: '',
            ...cost.pricing_info,
        },
    });
    const [saving, setSaving] = useState(false);

    const handleChange = (key, value) => setFormData(prev => ({ ...prev, [key]: value }));
    const handlePricing = (key, value) => setFormData(prev => ({
        ...prev, pricing_info: { ...prev.pricing_info, [key]: value },
    }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try { await onSave(formData); } finally { setSaving(false); }
    };

    return (
        <Modal open onClose={onClose} title={isEdit ? `Edit: ${cost.model_name}` : 'Add Pricing Rule'} size="xl">
            <form onSubmit={handleSubmit}>
                <Modal.Body>
                    <Card title="General">
                        <div className="grid grid-cols-2 gap-3">
                            <Input label="Model Name" value={formData.model_name} onChange={(e) => handleChange('model_name', e.target.value)} placeholder="gemini-2.5-flash" required />
                            <Input label="Provider" value={formData.provider} onChange={(e) => handleChange('provider', e.target.value)} placeholder="google" required />
                            <Select label="Modality" options={MODALITY_OPTIONS} value={formData.modality} onChange={(e) => handleChange('modality', e.target.value)} />
                            <Input label="Tier Name" hint="leave empty for flat pricing" value={formData.tier_name} onChange={(e) => handleChange('tier_name', e.target.value)} placeholder="tier_1" />
                        </div>
                    </Card>
                    <Card title="Display">
                        <div className="grid grid-cols-2 gap-3">
                            <Input label="Display Name" hint="label shown to users" value={formData.display_name} onChange={(e) => handleChange('display_name', e.target.value)} placeholder="Gemini 2.5 Flash" />
                            <Input label="Display Order" hint="lower = higher in list" type="number" value={formData.display_order} onChange={(e) => handleChange('display_order', e.target.value)} />
                            <div className="col-span-2 flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="visible_to_user"
                                    checked={formData.visible_to_user}
                                    onChange={(e) => handleChange('visible_to_user', e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <label htmlFor="visible_to_user" className="text-sm text-gray-700">
                                    Visible to user in model selector
                                </label>
                            </div>
                        </div>
                    </Card>
                    <Card title="Pricing">
                        <div className="grid grid-cols-2 gap-3">
                            <Input label="Input Cost per Unit ($)" type="number" step="any" value={formData.pricing_info.input_cost_per_unit} onChange={(e) => handlePricing('input_cost_per_unit', e.target.value)} required />
                            <Input label="Output Cost per Unit ($)" type="number" step="any" value={formData.pricing_info.output_cost_per_unit} onChange={(e) => handlePricing('output_cost_per_unit', e.target.value)} required />
                            <Input label="Cache Cost per Unit ($)" type="number" step="any" value={formData.pricing_info.cache_cost_per_unit} onChange={(e) => handlePricing('cache_cost_per_unit', e.target.value)} placeholder="Optional" />
                            <Input label="Base Unit Size" type="number" value={formData.pricing_info.base_unit_size} onChange={(e) => handlePricing('base_unit_size', e.target.value)} />
                            <Input label="Up To Tokens" hint="for tiered pricing" type="number" value={formData.pricing_info.up_to_tokens} onChange={(e) => handlePricing('up_to_tokens', e.target.value)} placeholder="Optional" />
                        </div>
                    </Card>
                    <Card title="Notes">
                        <Textarea rows={3} value={formData.notes} onChange={(e) => handleChange('notes', e.target.value)} placeholder="Additional notes about this pricing rule..." />
                    </Card>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
                    <Button type="submit" loading={saving}>{isEdit ? 'Save Changes' : 'Create'}</Button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
